import {
    AlertIssueChild,
    AlertResponse,
    AssemblyAlertItem,
    IssueResponseBase,
    type IssueResponseBaseUnion,
} from '@kcftech/prometheus-client';
import {
    type AnyMemberField,
    type AnyMemberKey,
    type NormalizedWith,
    projection,
    type SdkClass,
} from './normalizedTypes';

// The kinds of entity that the normalized cache shares between queries. To
// add one, add its SDK types to `EntitySdkTypes` and the SDK classes its objects
// are instances of to `ENTITY_CLASSES`. Its objects must have a string `Id`,
// unique within the kind. SDK classes embedding an entity under other field
// names go in `PROJECTIONS`, and fields holding another entity in
// `ENTITY_REF_FIELDS`.

/**
 * SDK types of the entities, by kind. A kind with several types is returned in
 * different shapes by different endpoints; they merge into one entity, each
 * query keeping only its own shape's fields.
 */
type EntitySdkTypes = {
    issue: IssueResponseBaseUnion;
    // Mission Control's `AssemblyAlertItem` holds its Issue as `IssueId`, rather
    // than as `Issue`
    alert: AlertResponse | AssemblyAlertItem;
};

export type EntityKind = keyof EntitySdkTypes;

/** The SDK classes that each kind's entities are instances of. */
export const ENTITY_CLASSES: Readonly<Record<EntityKind, readonly SdkClass[]>> = {
    issue: [IssueResponseBase],
    alert: [AlertResponse, AssemblyAlertItem],
};

/** SDK classes that are projections of an entity under other field names. */
export const PROJECTIONS = [
    // an Alert's embedded Issue
    projection('issue' satisfies EntityKind, AlertIssueChild, {
        IssueId: 'Id',
        IssueSeverity: 'Severity',
        IssueDescription: 'Description',
    }),
] as const;

/**
 * SDK data as `toNormalized` returns it: plain objects at every depth, with
 * entity projections renamed to their entity's field names.
 */
export type Normalized<T> = NormalizedWith<T, (typeof PROJECTIONS)[number]>;

/** `AlertIssueChild` as normalized: plain, with the Issue's field names. */
export type NormalizedAlertIssueChild = Normalized<AlertIssueChild>;

/** Normalized entity types, by kind. */
export type EntityKinds = {
    [K in EntityKind]: Normalized<EntitySdkTypes[K]>;
};

/** The fields of `T` that can hold an entity: objects with a string `Id`. */
type EntityHoldingField<T> = {
    [F in AnyMemberKey<T>]: NonNullable<AnyMemberField<T, F>> extends { Id: string }
        ? F
        : never;
}[AnyMemberKey<T>];

/**
 * Fields of an entity that hold another entity, with that entity's kind.
 * Entity updates set them to the held entity by its `Id`, so that they follow
 * that entity's updates.
 */
export const ENTITY_REF_FIELDS = {
    alert: { Issue: 'issue' },
} as const satisfies {
    readonly [K in EntityKind]?: {
        readonly [F in EntityHoldingField<EntityKinds[K]>]?: EntityKind;
    };
};
